import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import LegacyHeader from './header.jsx';
import { useAuth } from '../AuthContext';
import { signIn, fetchAuthSession } from '@aws-amplify/auth';

const LegacyHealingLogin = ({
  // Text content props (keep all existing defaults)
  welcomeText = "Welcome to",
  brandName = "Legacy Healing",
  suffixText = "Portal",
  description = "Please sign in to access your account",
  confidentialityText = "All information is confidential and securely stored in compliance with Legacy Healing.",
  ctaText = "Sign In",

  // Form fields (keep all existing defaults)
  emailLabel = "Username",
  passwordLabel = "Password",
  rememberMeText = "Remember me",
  forgotPasswordText = "Forgot password?",
  noAccountText = "Don't have an account?",
  signUpText = "Sign up",

  // Style props (keep all existing defaults)
  primaryColor = "#1F3047",
  secondaryColor = "#349AC8",
  textColor = "#1F3047",
  welcomeFontSize = 32,
  brandFontSize = 40,
  suffixFontSize = 32,
  bodyFontSize = 16,

  // Logo props (keep all existing defaults)
  logo = null,
  logoHeight = 60,
  onForgotPassword = () => alert('Forgot password clicked'),
  onSignUp = () => alert('Sign up clicked')
}) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Hardcoded credentials (for demo only)
  const validCredentials = [
    { email: "IT", password: "Legacy1" },
    { email: "Team", password: "Healing1" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {

      const user = await signIn({ username: email, password });
      console.log("user", user)
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      const refreshToken = session.tokens?.refreshToken?.toString();
      const accessToken = session.tokens?.accessToken?.toString();

      login(rememberMe, {
        email: user.attributes?.email || email,
        accessToken,
        idToken,
        refreshToken,
      });

      navigate('/');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };


  // Keep all existing style objects exactly as they were
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto',
    padding: 'clamp(20px, 5vw, 40px) clamp(16px, 4vw, 20px)',
    textAlign: 'center',
    boxSizing: 'border-box',
  };

  // ... (keep all other style objects exactly the same)

  return (
    <>
      <LegacyHeader />

      <div style={containerStyle}>
        {/* Logo (conditionally rendered) */}
        {logo && (
          <div style={logoContainerStyle}>
            <img
              src={logo}
              alt={`${brandName} Logo`}
              style={{
                ...logoStyle,
                height: `${logoHeight}px`,
                '@media (max-width: 768px)': {
                  height: `${logoHeight * 0.8}px`
                }
              }}
            />
          </div>
        )}

        {/* Welcome Message */}
        <div style={messageContainerStyle}>
          <div style={textRowStyle}>
            <span style={{
              ...textStyle,
              color: primaryColor,
              fontSize: `clamp(24px, 4vw, ${welcomeFontSize}px)`,
              fontFamily: 'Mauren',
              fontWeight: 100,
            }}>
              {welcomeText}
            </span>

            <span style={{
              ...textStyle,
              color: secondaryColor,
              fontSize: `clamp(28px, 5vw, ${brandFontSize}px)`,
              fontFamily: "'Turbinado Pro'",
              fontWeight: 400,
            }}>
              {brandName}
            </span>

            <span style={{
              ...textStyle,
              color: primaryColor,
              fontSize: `clamp(24px, 4vw, ${suffixFontSize}px)`,
              fontFamily: 'Mauren, sans-serif',
              fontWeight: 100,
            }}>
              {suffixText}
            </span>
          </div>

          {/* Description */}
          <div style={descriptionContainerStyle}>
            <div style={{
              ...paragraphStyle,
              fontSize: `clamp(14px, 3vw, ${bodyFontSize}px)`,
              lineHeight: '1.5',
              marginBottom: '24px'
            }}>
              {description}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              color: '#ff4444',
              backgroundColor: '#ffebee',
              padding: '12px',
              borderRadius: '4px',
              marginBottom: '16px',
              fontFamily: "'FONTSPRING DEMO - Commuters Sans'",
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} style={formStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>{emailLabel}</label>
              <input
                type="Username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={inputStyle}
                placeholder="IT Team"
              />
            </div>

            <div style={inputGroupStyle}>
              <label style={labelStyle}>{passwordLabel}</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={inputStyle}
                placeholder="••••••••"
              />
            </div>

            <div style={optionsContainerStyle}>
              <div
                style={rememberMeStyle}
                onClick={() => setRememberMe(!rememberMe)}
              >
                <div style={{
                  ...checkboxStyle,
                  outline: `1px ${primaryColor} solid`,
                  backgroundColor: rememberMe ? primaryColor : 'transparent'
                }}>
                  {rememberMe && <div style={checkmarkStyle} />}
                </div>
                <span style={rememberMeTextStyle}>{rememberMeText}</span>
              </div>

              {/* <button 
              type="button"
              onClick={onForgotPassword}
              style={forgotPasswordStyle}
            >
              {forgotPasswordText}
            </button> */}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              style={{
                ...buttonStyle,
                backgroundColor: secondaryColor,
                cursor: isLoading ? 'wait' : 'pointer',
                opacity: isLoading ? 0.7 : 1
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <span>Signing In...</span>
              ) : (
                <span>{ctaText}</span>
              )}
            </button>
          </form>

          {/* Confidentiality Notice */}
          <div style={{
            ...paragraphStyle,
            fontSize: `clamp(12px, 2vw, 14px)`,
            marginTop: '24px',
            color: '#666'
          }}>
            {confidentialityText}
          </div>
        </div>
      </div>
    </>
  );
};

// PropTypes (keep all existing prop types)
LegacyHealingLogin.propTypes = {
  welcomeText: PropTypes.string,
  brandName: PropTypes.string,
  suffixText: PropTypes.string,
  description: PropTypes.string,
  confidentialityText: PropTypes.string,
  ctaText: PropTypes.string,
  emailLabel: PropTypes.string,
  passwordLabel: PropTypes.string,
  rememberMeText: PropTypes.string,
  forgotPasswordText: PropTypes.string,
  noAccountText: PropTypes.string,
  signUpText: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  textColor: PropTypes.string,
  welcomeFontSize: PropTypes.number,
  brandFontSize: PropTypes.number,
  suffixFontSize: PropTypes.number,
  bodyFontSize: PropTypes.number,
  logo: PropTypes.string,
  logoHeight: PropTypes.number,
  onForgotPassword: PropTypes.func,
  onSignUp: PropTypes.func
};

export default LegacyHealingLogin;



const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '800px',
  margin: '0 auto',
  padding: 'clamp(20px, 5vw, 40px) clamp(16px, 4vw, 20px)',
  textAlign: 'center',
  boxSizing: 'border-box',
};

const logoContainerStyle = {
  marginBottom: 'clamp(20px, 4vw, 30px)',
};

const logoStyle = {
  width: 'auto',
  maxWidth: '100%',
};

const messageContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'clamp(16px, 3vw, 24px)',
  width: '100%',
};

const textRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 'clamp(8px, 2vw, 16px)',
};

const textStyle = {
  wordWrap: 'break-word',
  whiteSpace: 'nowrap',
  margin: 0,
  lineHeight: 1.2,
  '@media (max-width: 480px)': {
    whiteSpace: 'normal'
  }
};

const descriptionContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'clamp(4px, 1vw, 8px)',
  width: '100%',
};

const paragraphStyle = {
  color: '#1F3047',
  fontFamily: "'FONTSPRING DEMO - Commuters Sans'",
  fontWeight: 400,
  wordWrap: 'break-word',
  textAlign: 'center',
  margin: 0,
};

const checkboxStyle = {
  width: 'clamp(20px, 4vw, 24px)',
  height: 'clamp(20px, 4vw, 24px)',
  minWidth: 'clamp(20px, 4vw, 24px)',
  borderRadius: '4px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'all 0.2s ease',
  marginTop: '2px'
};

const checkmarkStyle = {
  width: 'clamp(12px, 3vw, 15px)',
  height: 'clamp(8px, 2vw, 10px)',
  borderBottom: '2px solid white',
  borderLeft: '2px solid white',
  transform: 'rotate(-45deg)',
  marginBottom: '2px',
};

const buttonStyle = {
  padding: 'clamp(10px, 2vw, 12px) clamp(16px, 3vw, 24px)',
  color: 'white',
  fontSize: 'clamp(14px, 3vw, 16px)',
  fontWeight: '500',
  border: 'none',
  borderRadius: '20px',
  transition: 'all 0.3s ease',
  marginTop: 'clamp(16px, 3vw, 20px)',
  width: 'clamp(140px, 30vw, 200px)',
  maxWidth: '300px',
  ':hover': {
    opacity: 0.9,
    transform: 'translateY(-2px)',
  },
  '@media (max-width: 480px)': {
    width: '80%',
    maxWidth: 'none'
  }
};

const formStyle = {
  width: '100%',
  maxWidth: '400px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px'
};

const inputGroupStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%'
};

const labelStyle = {
  color: '#1F3047',
  fontFamily: "'FONTSPRING DEMO - Commuters Sans'",
  fontWeight: 500,
  fontSize: '14px',
  textAlign: 'left'
};

const inputStyle = {
  padding: '12px 16px',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: "'FONTSPRING DEMO - Commuters Sans'",
  transition: 'border 0.3s ease',
  ':focus': {
    outline: 'none',
    borderColor: '#349AC8',
    boxShadow: '0 0 0 2px rgba(52, 154, 200, 0.2)'
  }
};

const optionsContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginBottom: '8px'
};

const rememberMeStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer'
};

const rememberMeTextStyle = {
  fontFamily: "'FONTSPRING DEMO - Commuters Sans'",
  fontSize: '14px',
  color: '#1F3047'
};

const forgotPasswordStyle = {
  background: 'none',
  border: 'none',
  color: '#349AC8',
  fontFamily: "'FONTSPRING DEMO - Commuters Sans'",
  fontSize: '14px',
  fontWeight: 500,
  cursor: 'pointer',
  padding: '4px',
  ':hover': {
    textDecoration: 'underline'
  }
};

const signUpContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  marginTop: '24px'
};

const noAccountTextStyle = {
  fontFamily: "'FONTSPRING DEMO - Commuters Sans'",
  fontSize: '14px',
  color: '#666'
};

const signUpButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#349AC8',
  fontFamily: "'FONTSPRING DEMO - Commuters Sans'",
  fontSize: '14px',
  fontWeight: 600,
  cursor: 'pointer',
  padding: '4px',
  ':hover': {
    textDecoration: 'underline'
  }
};
